import pagosEgresosService from '@/services/pagosEgresosService';

/**
 * Flat, filterable, paginated listing of individual egreso payments
 * (Pagos tab in Egresos, `s9-tab-pagos-plano`). Own namespaced module —
 * `egresos` has no dedicated Vuex module yet, so this does not extend
 * `cuentasEmpresa.js` (a different domain).
 *
 * Fetch (listing) and export keep SEPARATE loading/error state on purpose:
 * triggering an Excel export must not blank out the table rendered behind it,
 * mirroring the `cuentasEmpresa.js` convention for corte/estadoCuenta.
 */

const filtrosPorDefecto = () => ({
  fecha_desde: null,
  fecha_hasta: null,
  id_proveedor: null,
  id_cuenta: null,
  id_medio_pago: null,
  numero_comprobante: null,
  // Backend default when omitted; kept explicit here so the UI's filter
  // panel always has a defined value to bind to.
  estado_anulacion: 'todos',
});

const paginacionPorDefecto = () => ({
  pagina_actual: 1,
  total_paginas: 1,
  total_registros: 0,
  registros_por_pagina: 50,
});

const resumenPorDefecto = () => ({
  total_pagado: 0,
  total_anulado: 0,
  neto: 0,
  cantidad_pagos: 0,
  cantidad_anulados: 0,
});

const state = () => ({
  items: [],
  headers: [],
  paginacion: paginacionPorDefecto(),
  resumen: resumenPorDefecto(),
  filtros: filtrosPorDefecto(),
  filtrosAplicados: {},
  loading: false,
  error: null,
  exportando: false,
  exportError: null,
  anulando: false,
  anularError: null,
});

const getters = {
  // Drives the "clear filters" button in the Pagos tab (WU5): disabled while
  // filtros still match the defaults.
  hayFiltrosActivos: (state) => {
    const defaults = filtrosPorDefecto();
    return Object.keys(defaults).some((key) => state.filtros[key] !== defaults[key]);
  },
};

/**
 * `GET /egresos/pagos` returns 400 in a few shapes: express-validator's
 * `{ errors: [{ msg }] }` for request validation, `{ message }` for
 * business-rule rejections (e.g. reversed/oversized date range), or
 * `{ msg }` (e.g. export-with-no-results, see pago_egreso_controller.js).
 * All must surface as a single readable string instead of leaking either
 * shape to the view.
 */
const extraerMensajeError = (error, mensajePorDefecto) => {
  const data = error.response?.data;
  if (Array.isArray(data?.errors) && data.errors.length) {
    return data.errors.map((e) => e.msg || e.message).join(', ');
  }
  return data?.message || data?.msg || error.message || mensajePorDefecto;
};

const actions = {
  async fetchListadoPagos({ commit, state }, opciones = {}) {
    const { page = 1, limit = 50, ...filtrosOverride } = opciones;
    const filtros = { ...state.filtros, ...filtrosOverride };
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      const { data } = await pagosEgresosService.obtenerListado({ ...filtros, page, limit });
      commit('SET_ITEMS', data.data || []);
      commit('SET_HEADERS', data.headers || []);
      commit('SET_PAGINACION', data.paginacion || paginacionPorDefecto());
      commit('SET_RESUMEN', data.resumen || resumenPorDefecto());
      commit('SET_FILTROS_APLICADOS', data.filtros_aplicados || {});
      commit('SET_FILTROS', filtros);
      return data;
    } catch (error) {
      // No dejar la tabla/resumen anteriores (de un fetch previo exitoso) mostrados
      // debajo del banner de error — no toca `filtros`, el usuario debe poder corregirlos.
      commit('SET_ITEMS', []);
      commit('SET_PAGINACION', paginacionPorDefecto());
      commit('SET_RESUMEN', resumenPorDefecto());
      commit('SET_ERROR', extraerMensajeError(error, 'Error al cargar el listado de pagos'));
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Export ignores pagination server-side (all matching rows, not just the
   * current page) — `page`/`limit` are stripped before calling the service.
   */
  async exportarListadoPagos({ commit, state }, opciones = {}) {
    const filtrosOverride = { ...opciones };
    delete filtrosOverride.page;
    delete filtrosOverride.limit;
    const filtros = { ...state.filtros, ...filtrosOverride };
    try {
      commit('SET_EXPORTANDO', true);
      commit('SET_EXPORT_ERROR', null);
      const { data } = await pagosEgresosService.exportarListado(filtros);
      return data;
    } catch (error) {
      commit('SET_EXPORT_ERROR', extraerMensajeError(error, 'Error al exportar el listado de pagos'));
      throw error;
    } finally {
      commit('SET_EXPORTANDO', false);
    }
  },

  actualizarFiltros({ commit, state }, filtrosParciales = {}) {
    commit('SET_FILTROS', { ...state.filtros, ...filtrosParciales });
  },

  resetFiltros({ commit }) {
    commit('RESET_FILTROS');
  },

  /**
   * Anula un pago (POST /egresos/pagos/:id/anular, body { motivo }).
   * No toca `items`/`paginacion`/`resumen`: el llamador (TablaPagos) es
   * responsable de refetchear el listado tras el éxito para que la fila
   * anulada y el resumen recalculado se vean reflejados.
   */
  async anularPago({ commit }, { id, motivo }) {
    try {
      commit('SET_ANULANDO', true);
      commit('SET_ANULAR_ERROR', null);
      const { data } = await pagosEgresosService.anularPago(id, motivo);
      return data.message;
    } catch (error) {
      commit('SET_ANULAR_ERROR', extraerMensajeError(error, 'Error al anular el pago'));
      throw error;
    } finally {
      commit('SET_ANULANDO', false);
    }
  },
};

const mutations = {
  SET_ITEMS(s, v) { s.items = v; },
  SET_HEADERS(s, v) { s.headers = v; },
  SET_PAGINACION(s, v) { s.paginacion = v; },
  SET_RESUMEN(s, v) { s.resumen = v; },
  SET_FILTROS_APLICADOS(s, v) { s.filtrosAplicados = v; },
  SET_FILTROS(s, v) { s.filtros = v; },
  RESET_FILTROS(s) { s.filtros = filtrosPorDefecto(); },
  SET_LOADING(s, v) { s.loading = v; },
  SET_ERROR(s, v) { s.error = v; },
  SET_EXPORTANDO(s, v) { s.exportando = v; },
  SET_EXPORT_ERROR(s, v) { s.exportError = v; },
  SET_ANULANDO(s, v) { s.anulando = v; },
  SET_ANULAR_ERROR(s, v) { s.anularError = v; },
};

export default { namespaced: true, state, getters, actions, mutations };
